import type { SimulationObjective } from "../api-contracts/simulations.js";

// REGISTRY DE OBJETIVOS da busca automática (fonte única, pura, consumida por worker
// [ranqueamento] e web [exibição]). O objetivo escolhido exerce influência TOTAL: define a métrica
// de aptidão, se a META é atingida, a ordem dos candidatos e o que conta como ganho sobre a base.
// Adicionar um objetivo = novo valor no enum + uma entrada em OBJECTIVE_STRATEGIES (o Record tipado
// FORÇA a implementação — sem retrabalho espalhado). A confiança NUNCA reordena (só avisa na UI).

/** Avaliação de um candidato/base — suficiente para todos os objetivos. */
export interface ObjectiveEval {
  /**
   * ANULÁVEL. Sem plano declarado o motor devolve OEE nulo, e `Simulation.
   * results` é `Json`: quem lê faz cast, então o tipo era a ÚNICA defesa e mentia. Declará-lo
   * `number` fazia `c.oee > base.oee` comparar com `null`, que o JS coage para `0` — um candidato
   * "vencia" uma base que ninguém mediu. Quem consome DEVE tratar o nulo (e o não-finito) como
   * ausência, nunca como zero.
   */
  oee: number | null;
  estimatedProduction: number;
  horizonHours: number;
  /** Σ|deltaPct| dos ajustes aplicados (0 = base). Critério de MENOR ESFORÇO p/ atingir a meta. */
  effort: number;
  /**
   * Instante em que a produção acumulada cruzou o alvo, em SEGUNDOS no eixo
   * OPERACIONAL (o motor colapsa o tempo quando há expediente). `null` em rodadas antigas,
   * quando não houve alvo, ou quando nenhuma réplica cruzou.
   */
  timeToTargetS?: number | null;
  /** A maioria das réplicas cruzou o alvo dentro do teto de horizonte. */
  targetReached?: boolean;
}

export interface ObjectiveContext {
  /** Meta de quantidade; null quando o objetivo não usa meta (ou não foi informada). */
  targetQuantity: number | null;
  /**
   * Prazo da OP em segundos, RELATIVO À ÂNCORA do período e no mesmo eixo operacional
   * de `timeToTargetS` (nunca relógio civil). `null` fora do objetivo OP_ON_TIME.
   */
  dueAtS?: number | null;
  /**
   * Saldo a produzir quando a OP já está em andamento (planejado − produzido bom).
   * `null` para OP agendada (o alvo é a quantidade planejada integral) e fora de OP_ON_TIME.
   */
  remainingQuantity?: number | null;
}

export interface ObjectiveMetricView {
  value: number | null;
  // "min" entra para os objetivos de SEQUÊNCIA (atraso/setup em minutos).
  unit: "%" | "un" | "h" | "min";
  label: string;
}

export interface ObjectiveStrategy {
  id: SimulationObjective;
  label: string;
  /** Precisa de quantidade-alvo (habilita o campo na UI). */
  needsTarget: boolean;
  /**
   * A direção de "melhor" é do REGISTRY, nunca inferida da unidade pela UI: OP_ON_TIME
   * (folga) e TIME_TO_QUANTITY (tempo) compartilham a unidade "h" com sinais opostos, então
   * `unit === "h" ⇒ menor é melhor` pintava o melhor candidato de OP_ON_TIME como piora.
   */
  higherIsBetter: boolean;
  /** Métrica-título do candidato para este objetivo (exibição). */
  metric(e: ObjectiveEval, ctx: ObjectiveContext): ObjectiveMetricView;
  /** O candidato atinge a meta? null quando o objetivo não tem meta. */
  meetsTarget(e: ObjectiveEval, ctx: ObjectiveContext): boolean | null;
  /** Ordena candidatos "melhor primeiro" (total, determinístico; degenerados ao fim). */
  compare(a: ObjectiveEval, b: ObjectiveEval, ctx: ObjectiveContext): number;
  /** O candidato é ganho real sobre a base? (senão o lote fecha NO_GAIN). */
  beatsBaseline(candidate: ObjectiveEval, baseline: ObjectiveEval, ctx: ObjectiveContext): boolean;
}

/** Esforço de um candidato = soma das magnitudes dos ajustes (independe de sinal). */
export function candidateEffort(changes: ReadonlyArray<{ deltaPct: number }>): number {
  return changes.reduce((sum, c) => sum + Math.abs(c.deltaPct), 0);
}

// Ordena por número (asc/desc) empurrando não-finitos SEMPRE ao fim (contrato de sort estável).
/** `null` e `NaN` são a MESMA coisa aqui: ausência de medida, nunca zero. */
function finiteOrNull(v: number | null | undefined): number | null {
  return v != null && Number.isFinite(v) ? v : null;
}

function byNumber(a: number | null, b: number | null, dir: "asc" | "desc"): number {
  const aBad = a == null || !Number.isFinite(a);
  const bBad = b == null || !Number.isFinite(b);
  if (aBad || bBad) return aBad === bBad ? 0 : aBad ? 1 : -1;
  if (a === b) return 0;
  return dir === "asc" ? a - b : b - a;
}

function rate(e: ObjectiveEval): number {
  return e.horizonHours > 0 ? e.estimatedProduction / e.horizonHours : 0;
}

/**
 * Extrapolação LINEAR (alvo ÷ ritmo médio) — assume ritmo constante, ignora rampa, paradas
 * concentradas e expediente. Isto é FALLBACK: só vale para candidatos antigos,
 * que não carregam o instante medido pelo motor. Ver `hoursToTarget`.
 */
function linearTimeToTarget(e: ObjectiveEval, ctx: ObjectiveContext): number {
  const qty = ctx.targetQuantity ?? e.estimatedProduction;
  const r = rate(e);
  return r > 0 ? qty / r : Number.POSITIVE_INFINITY;
}

/**
 * Horas até a meta. Prefere o instante MEDIDO pelo motor (que respeita fila, bloqueio e
 * expediente); cai na extrapolação linear só quando o candidato não o carrega.
 */
export function hoursToTarget(e: ObjectiveEval, ctx: ObjectiveContext): number {
  const measured = e.timeToTargetS;
  if (measured != null && Number.isFinite(measured)) return measured / 3600;
  return linearTimeToTarget(e, ctx);
}

/** O valor exibido veio de extrapolação, não do motor? A UI rotula esses casos como estimativa. */
export function isTimeEstimated(e: ObjectiveEval): boolean {
  return e.timeToTargetS == null || !Number.isFinite(e.timeToTargetS);
}

/** Folga (h) sobre o prazo: positivo = adiantado, negativo = atraso. NaN quando falta dado. */
function slackHours(e: ObjectiveEval, ctx: ObjectiveContext): number {
  const due = ctx.dueAtS;
  if (due == null || !Number.isFinite(due)) return Number.NaN;
  const t = e.timeToTargetS;
  if (t == null || !Number.isFinite(t)) return Number.NaN;
  return (due - t) / 3600;
}

/** Atinge a meta = produz ≥ alvo no horizonte. Sem meta ⇒ tudo "viável" (cai p/ a métrica). */
function reaches(e: ObjectiveEval, ctx: ObjectiveContext): boolean {
  if (ctx.targetQuantity == null) return true;
  return Number.isFinite(e.estimatedProduction) && e.estimatedProduction >= ctx.targetQuantity;
}

// Ordem "atingir a meta com o MENOR esforço": viáveis primeiro; entre viáveis, menor esforço
// (empate → maior produção); entre inviáveis, mais perto da meta (empate → menor esforço).
// SEM META a pergunta deixa de ser "atingir com menor esforço" e passa a ser "quanto
// dá para produzir na janela". `reaches` devolve true para todos nesse caso, então o desempate por
// menor esforço colocava o candidato que MENOS mexe em primeiro lugar — ou seja, recomendava não
// fazer nada. Sem meta a ordenação primária é produção decrescente.
function compareByAttainment(a: ObjectiveEval, b: ObjectiveEval, ctx: ObjectiveContext): number {
  if (ctx.targetQuantity == null) {
    const byProd = byNumber(a.estimatedProduction, b.estimatedProduction, "desc");
    return byProd !== 0 ? byProd : byNumber(a.effort, b.effort, "asc");
  }
  const fa = reaches(a, ctx);
  const fb = reaches(b, ctx);
  if (fa !== fb) return fa ? -1 : 1;
  if (fa) {
    const byEffort = byNumber(a.effort, b.effort, "asc");
    return byEffort !== 0
      ? byEffort
      : byNumber(a.estimatedProduction, b.estimatedProduction, "desc");
  }
  const byProd = byNumber(a.estimatedProduction, b.estimatedProduction, "desc");
  return byProd !== 0 ? byProd : byNumber(a.effort, b.effort, "asc");
}

function beatsBaselineByAttainment(
  c: ObjectiveEval,
  base: ObjectiveEval,
  ctx: ObjectiveContext
): boolean {
  if (ctx.targetQuantity == null) return c.estimatedProduction > base.estimatedProduction;
  if (reaches(base, ctx)) return false; // a base já atinge a meta ⇒ nenhuma mudança necessária
  if (reaches(c, ctx)) return true; // o candidato passa a atingir a meta
  return c.estimatedProduction > base.estimatedProduction; // aproxima da meta inatingível
}

const OEE_IMPROVEMENT: ObjectiveStrategy = {
  id: "OEE_IMPROVEMENT",
  label: "OEE projetado",
  needsTarget: false,
  higherIsBetter: true,
  metric: (e) => ({
    value: finiteOrNull(e.oee),
    unit: "%",
    label: "OEE projetado",
  }),
  meetsTarget: () => null,
  compare: (a, b) => byNumber(a.oee, b.oee, "desc"),
  // Os DOIS lados precisam existir. `c.oee > base.oee` com `base.oee` nulo virava
  // `40 > 0` (coerção do JS) e o lote declarava ganho contra uma base que nunca foi medida; o
  // simétrico (`null > 40`) fechava NO_GAIN sem dizer por quê. Sem número dos dois lados não há
  // comparação a fazer — é a mesma regra que o `byNumber` do `compare` já aplica.
  beatsBaseline: (c, base) => {
    const cur = finiteOrNull(c.oee);
    const ref = finiteOrNull(base.oee);
    return cur != null && ref != null && cur > ref;
  },
};

// Responde "quanto eu produzo nesta janela". A meta virou OPCIONAL: com meta, mantém a
// semântica de atingir com menor esforço (pill "Meta atingida"); sem meta, maximiza produção.
const TARGET_QUANTITY: ObjectiveStrategy = {
  id: "TARGET_QUANTITY",
  label: "Produção na janela",
  needsTarget: false,
  higherIsBetter: true,
  metric: (e) => ({
    value: Number.isFinite(e.estimatedProduction) ? e.estimatedProduction : null,
    unit: "un",
    label: "Produção na janela",
  }),
  meetsTarget: (e, ctx) => (ctx.targetQuantity == null ? null : reaches(e, ctx)),
  compare: compareByAttainment,
  beatsBaseline: beatsBaselineByAttainment,
};

// O tempo passa a vir do MOTOR (instante em que a produção cruza o alvo, respeitando
// fila, bloqueio e expediente). A extrapolação linear sobrevive só como fallback de candidatos
// antigos — ver `hoursToTarget`/`isTimeEstimated`.
const TIME_TO_QUANTITY: ObjectiveStrategy = {
  id: "TIME_TO_QUANTITY",
  label: "Tempo projetado",
  needsTarget: true,
  higherIsBetter: false,
  metric: (e, ctx) => {
    const t = hoursToTarget(e, ctx);
    // Sem arredondar aqui: quem arredonda é a exibição. Cortar em 0,1 h na origem
    // fazia cinco candidatos separados por 10, 9, 5, 0,3 e 0,1 minutos colidirem em dois números
    // no Δ vs base, apagando a ordem que o ranking existe para mostrar.
    return {
      value: Number.isFinite(t) ? t : null,
      unit: "h",
      label: "Tempo projetado",
    };
  },
  // `targetReached` é a verdade do motor; `reaches` cobre o candidato legado que não a carrega.
  meetsTarget: (e, ctx) =>
    ctx.targetQuantity == null ? null : (e.targetReached ?? reaches(e, ctx)),
  // Quem atinge vem antes; entre os que atingem, menor tempo.
  compare: (a, b, ctx) => {
    const fa = a.targetReached ?? reaches(a, ctx);
    const fb = b.targetReached ?? reaches(b, ctx);
    if (fa !== fb) return fa ? -1 : 1;
    return byNumber(hoursToTarget(a, ctx), hoursToTarget(b, ctx), "asc");
  },
  // Mesmo gate de `compare`: sem ele um candidato que NÃO cruzou a meta (e portanto cai na
  // extrapolação linear, otimista) podia "vencer" uma base que cruzou de fato — comparação entre
  // um número medido e um número inventado.
  beatsBaseline: (c, base, ctx) => {
    const fc = c.targetReached ?? reaches(c, ctx);
    const fb = base.targetReached ?? reaches(base, ctx);
    if (fc !== fb) return fc;
    return byNumber(hoursToTarget(c, ctx), hoursToTarget(base, ctx), "asc") < 0;
  },
};

// "a OP cabe no prazo?". A métrica é a FOLGA sobre o prazo: positiva = adiantado,
// negativa = atraso. Sem instante medido ou sem prazo a folga é indefinida, e o candidato vai ao
// fim da ordenação em vez de fingir um número.
const OP_ON_TIME: ObjectiveStrategy = {
  id: "OP_ON_TIME",
  label: "Folga sobre o prazo",
  needsTarget: false,
  // A métrica é FOLGA: mais folga é melhor, ao contrário do "tempo projetado" que divide a unidade.
  higherIsBetter: true,
  metric: (e, ctx) => {
    const s = slackHours(e, ctx);
    // Precisão cheia na fonte (ver TIME_TO_QUANTITY); a borda formata.
    return {
      value: Number.isFinite(s) ? s : null,
      unit: "h",
      label: "Folga sobre o prazo",
    };
  },
  meetsTarget: (e, ctx) => {
    const s = slackHours(e, ctx);
    if (!Number.isFinite(s)) return false;
    return (e.targetReached ?? false) && s >= 0;
  },
  // Quem ATINGE a meta vem antes — a folga é média só das réplicas que cruzaram, então um
  // candidato que entrega em 1 de 5 réplicas exibe folga melhor que um que entrega sempre.
  compare: (a, b, ctx) => {
    const fa = a.targetReached ?? false;
    const fb = b.targetReached ?? false;
    if (fa !== fb) return fa ? -1 : 1;
    return byNumber(slackHours(a, ctx), slackHours(b, ctx), "desc");
  },
  beatsBaseline: (c, base, ctx) => {
    const fc = c.targetReached ?? false;
    const fb = base.targetReached ?? false;
    if (fc !== fb) return fc;
    return byNumber(slackHours(c, ctx), slackHours(base, ctx), "desc") < 0;
  },
};

export const OBJECTIVE_STRATEGIES: Record<SimulationObjective, ObjectiveStrategy> = {
  OEE_IMPROVEMENT,
  TARGET_QUANTITY,
  TIME_TO_QUANTITY,
  OP_ON_TIME,
};

export function getObjectiveStrategy(id: SimulationObjective): ObjectiveStrategy {
  return OBJECTIVE_STRATEGIES[id];
}
